import React, { useMemo } from 'react';
import styled from '@emotion/styled';
import { Tab } from '@mui/material';
import { Tabs as MuiTabs } from '@styles/material-ui';
import { mq } from '@styles/breakpoints';
import { noop } from 'lodash';
import { TabGalleryImage } from '@model/common/image';

const Tabs = styled(MuiTabs)(({ theme }) => ({
  marginLeft: -theme.custom.spacing.large,
  marginRight: -theme.custom.spacing.large,
  ['.MuiTabs-flexContainer']: {
    gap: theme.custom.spacing.small,
    marginBottom: theme.custom.spacing.large,
    overflowX: 'auto',
    paddingLeft: theme.custom.spacing.large,
    paddingRight: theme.custom.spacing.large,
    ['::-webkit-scrollbar']: { display: 'none' },
    [mq.small]: {
      justifyContent: 'center'
    }
  },
  ['.MuiTabs-indicator']: {
    display: 'none'
  },
  ['.MuiTab-root']: {
    alignItems: 'center',
    border: `1px solid ${theme.custom.colors.group2.base}`,
    borderRadius: 30,
    color: theme.custom.colors.group2.base,
    display: 'flex',
    flexDirection: 'row',
    minHeight: 'auto',
    padding: '8px 16px',
    textTransform: 'none',
    whiteSpace: 'nowrap',
    ['i']: {
      color: theme.custom.colors.group2.base,
      fontSize: '2rem',
      marginBottom: 0,
      marginRight: theme.custom.spacing.xSmall
    },
    ['&.Mui-selected']: {
      backgroundColor: theme.custom.colors.group2.base,
      color: theme.custom.colors.white,
      ['i']: {
        color: theme.custom.colors.white
      }
    }
  }
}));

export interface GalleryTabsProps {
  galleryData: Array<TabGalleryImage>;
  currentTabIndex: number;
  onTabChange: (event: React.SyntheticEvent, newValue: number) => void;
}

export const GalleryTabs: React.FC<GalleryTabsProps> = ({ galleryData, currentTabIndex, onTabChange }) => {
  const tabs = useMemo(() => {
    const categories = galleryData
      .map((item) => item.category)
      .filter(
        (category, index, self) => category !== null && category !== undefined && self.indexOf(category) === index
      );
    if (categories.length === 0) {
      return [];
    }
    return ['View All', ...categories];
  }, [galleryData]);

  if (tabs.length === 0) {
    return null;
  }

  return (
    <Tabs
      value={currentTabIndex}
      onChange={onTabChange}
      variant="scrollable"
      scrollButtons="auto"
      allowScrollButtonsMobile
    >
      {tabs.map((tab, index) => (
        <Tab key={index} label={tab} value={index} />
      ))}
    </Tabs>
  );
};

export const getFilteredImages = (
  galleryData: Array<TabGalleryImage>,
  currentTabIndex: number
): Array<TabGalleryImage> => {
  const categories = galleryData
    .map((item) => item.category)
    .filter((category, index, self) => category !== null && category !== undefined && self.indexOf(category) === index);

  const tabs = categories.length > 0 ? ['View All', ...categories] : [];

  if (tabs.length === 0 || currentTabIndex === 0) {
    return galleryData; // Show all images
  }

  const selectedCategory = tabs[currentTabIndex];
  return galleryData.filter((item) => item.category === selectedCategory);
};

// Utility function to handle tab change with smooth scrolling
export const handleTabChangeWithScroll = (
  event: React.SyntheticEvent,
  newValue: number,
  setCurrentTabIndex: (index: number) => void
) => {
  setCurrentTabIndex(newValue);
  try {
    const target = event.target as HTMLElement;
    target.parentElement?.scroll({
      left: target.offsetLeft - 20,
      behavior: 'smooth'
    });
  } catch (e) {
    noop(e);
  }
};

export const createImageRows = (filteredImages: Array<TabGalleryImage>): Array<Array<TabGalleryImage>> => {
  const rows: Array<Array<TabGalleryImage>> = [];
  let currentIndex = 0;

  while (currentIndex < filteredImages.length) {
    const rowIndex = rows.length;
    const isOddRow = rowIndex % 2 === 0;

    if (isOddRow) {
      rows.push([filteredImages[currentIndex]]);
      currentIndex++;
    } else {
      const imagesInRow: TabGalleryImage[] = filteredImages.slice(currentIndex, currentIndex + 2);
      rows.push(imagesInRow);
      currentIndex += imagesInRow.length;
    }
  }

  return rows;
};
